import { CaretDown, ListChecks, Question, Sparkle, Timer } from "@phosphor-icons/react";
import { DropdownMenu } from "radix-ui";
import { useTranslation } from "react-i18next";
import {
  groupActions,
  PLANNED_DO_ACTIONS,
  QUICK_ACTION_COUNT,
  type SelectionAction,
} from "~/features/documents/editor/selectionTypes";
import { Text } from "~/foundation/components/Text";

/**
 * The ✨ AI ▾ menu that lives in the selection toolbar.
 *
 * **Zero hardcoded actions.** Everything on offer comes from the configured catalogue — the
 * same list AI-actions settings edits — so somebody's Compact / Explain / Formalize and their
 * own custom actions appear here without this file knowing their names. That was a locked
 * decision upstream and it is the reason the menu is worth having at all.
 *
 * Two differences from the kms original, both deliberate (§F6):
 *  1. Its footer hardcoded "Default thinker" and a `🔒 Propose` badge — neither reflected the
 *     thinker the server resolved nor the lane it returned. Here the footer renders the lane
 *     the LAST `/ai-action` actually returned, or nothing at all.
 *  2. "Custom instruction…" only renders when the host supplies a handler. In kms the host
 *     never did, so that row has never appeared in production; a dead branch is not parity.
 */
export interface SelectionAiMenuProps {
  actions: SelectionAction[];
  /** Run a configured action over the current selection. */
  onSelectAction: (action: SelectionAction) => void;
  /** The workspace-acting entries (Taskize, Nudge…) — the host owns what each one does. */
  onDoAction: (action: SelectionAction) => void;
  /** Optional free-form instruction. Omit it and the row does not render. */
  onCustomAction?: () => void;
  /**
   * The lane the server returned for the last authorised action here, if any. `propose` means
   * results land in a review tray; `act` means they apply in place. Null ⇒ nothing to claim.
   */
  lastLane?: "act" | "propose" | null;
  disabled?: boolean;
}

export function SelectionAiMenu({
  actions,
  onSelectAction,
  onDoAction,
  onCustomAction,
  lastLane = null,
  disabled = false,
}: SelectionAiMenuProps) {
  const { t } = useTranslation();
  const { quick, doActions } = groupActions(actions);
  const quickShown = quick.slice(0, QUICK_ACTION_COUNT);
  const rest = quick.slice(QUICK_ACTION_COUNT);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={t("AI actions")}
          className="flex h-7 items-center gap-1 rounded-sm px-2 text-small font-medium text-accent hover:bg-hover-bg disabled:opacity-40"
        >
          <Sparkle size={14} aria-hidden />
          {t("AI")}
          <CaretDown size={10} aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={8}
          className="slate-pop z-50 flex w-[260px] flex-col gap-1 rounded-md border border-divider bg-menu-bg p-1 shadow-menu"
        >
          {quickShown.length > 0 ? (
            <div className="flex flex-wrap gap-1 p-1">
              {quickShown.map((action) => (
                <DropdownMenu.Item key={action.id} asChild>
                  <button
                    type="button"
                    title={action.prompt}
                    onClick={() => onSelectAction(action)}
                    className="cursor-pointer rounded-full border border-divider px-2 py-0.5 text-small text-text outline-none hover:bg-hover-bg data-[highlighted]:bg-menu-item-selected"
                  >
                    {action.title}
                  </button>
                </DropdownMenu.Item>
              ))}
            </div>
          ) : (
            <Text variant="small" className="p-2 text-text-tertiary">
              {t("No AI actions are configured yet.")}
            </Text>
          )}

          {rest.map((action) => (
            <DropdownMenu.Item key={action.id} asChild>
              <button
                type="button"
                title={action.prompt}
                onClick={() => onSelectAction(action)}
                className="flex h-8 cursor-pointer items-center rounded-sm px-3 text-body text-text outline-none data-[highlighted]:bg-menu-item-selected"
              >
                {action.title}
              </button>
            </DropdownMenu.Item>
          ))}

          {doActions.length > 0 ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-divider" />
              <DropdownMenu.Label className="px-3 py-1 text-small text-text-tertiary">
                {t("Do")}
              </DropdownMenu.Label>
              {doActions.map((action) => {
                const planned = PLANNED_DO_ACTIONS.has(action.id.replace(/^system-/, ""));
                return (
                  <DropdownMenu.Item key={action.id} asChild disabled={planned}>
                    <button
                      type="button"
                      disabled={planned}
                      title={action.prompt}
                      onClick={() => onDoAction(action)}
                      className="flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-sm px-3 text-body text-text outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-menu-item-selected"
                    >
                      <DoIcon id={action.id} />
                      {action.title}
                      {planned ? (
                        <span className="ms-auto rounded-full bg-bg-secondary px-1.5 text-small text-text-tertiary">
                          {t("soon")}
                        </span>
                      ) : null}
                    </button>
                  </DropdownMenu.Item>
                );
              })}
            </>
          ) : null}

          {onCustomAction ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-divider" />
              <DropdownMenu.Item asChild>
                <button
                  type="button"
                  onClick={onCustomAction}
                  className="flex h-8 cursor-pointer items-center rounded-sm px-3 text-body text-text outline-none data-[highlighted]:bg-menu-item-selected"
                >
                  {t("Custom instruction…")}
                </button>
              </DropdownMenu.Item>
            </>
          ) : null}

          {lastLane ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-divider" />
              <Text variant="small" className="px-3 py-1 text-text-tertiary">
                {lastLane === "propose"
                  ? t("Results arrive as a proposal you review before anything is written.")
                  : t("Results apply in place and stay provisional until you commit them.")}
              </Text>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function DoIcon({ id }: { id: string }) {
  const bare = id.replace(/^system-/, "");
  if (bare === "nudge") {
    return <Timer size={16} aria-hidden />;
  }
  if (bare === "ask") {
    return <Question size={16} aria-hidden />;
  }
  return <ListChecks size={16} aria-hidden />;
}
